// Shared Sentry configuration for JavaScript/React
//
// Architecture:
//   config/sentry.json                          # ← Single source of truth
//       ↓
//   ├─→ lib/sentry_config.rb                    # ← Ruby helper (reads JSON)
//   │   └─→ config/initializers/sentry.rb       # ← Rails Sentry init
//   │
//   ├─→ app/javascript/lib/sentry-config.js     # ← JS helper (imports JSON) [YOU ARE HERE]
//   │   └─→ app/javascript/layout/sentry.js     # ← React Sentry init
//   │
//   └─→ app/views/layouts/application.html.erb  # ← ERB template Sentry init
//
// This file provides helper functions for React to use the centralized config
import sentrySettings from "../../../config/sentry.json"

/**
 * Centralized Sentry configuration loaded from config/sentry.json
 * @typedef {Object} SentrySettings
 * @property {string} dsn - Sentry DSN
 * @property {string[]} deployableEnvironments - Environments where Sentry is enabled
 * @property {boolean} sendDefaultPii - Whether to send PII data
 * @property {{ sampleRate: number }} tracing - Tracing configuration
 * @property {string[]} tracePropagationTargets - Regex patterns for trace propagation
 * @property {{ sessionSampleRate: number, errorSampleRate: number, maskAllText: boolean, maskAllInputs: boolean, blockAllMedia: boolean }} replay - Session replay configuration
 * @property {{ autoTracking: boolean }} session - Session tracking configuration
 * @property {string[]} allowedHosts - Hosts where Sentry is allowed to report
 * @property {string[]} cdnDomains - CDN domains to exclude from span creation
 * @property {string[]} appDomains - App domains to include in span creation
 * @property {string[]} blocklistedPaths - Paths to exclude from span creation
 * @property {{ invalidErrors: string[], networkErrors: string[], sdkNoise: string[] }} errorFilters - Error filtering configuration
 */

/** @type {SentrySettings} */
export const SENTRY_CONFIG = sentrySettings

// Helper to check if current environment should report to Sentry
export function shouldEnableSentry() {
  const isE2ETest = process.env.CI_E2E_TEST === "true"
  const isDeployableEnv = SENTRY_CONFIG.deployableEnvironments.includes(
    process.env.BUGSNAG_ENV || process.env.NODE_ENV
  )

  return isDeployableEnv && !isE2ETest
}

/**
 * Shared error filter logic that can be used by both React and ERB
 * Pass the full event object to check stacktraces for webkit-masked-url
 * @param {Error} error - The error object
 * @param {string} errorName - The error name/type
 * @param {string} errorValue - The error message
 * @param {Object} [event] - The full Sentry event object
 * @returns {boolean} True if error should be filtered (not sent to Sentry)
 */
export function shouldFilterError(error, errorName, errorValue, event = null) {
  // Filter InvalidError with specific messages
  if (errorName === "InvalidError") {
    return SENTRY_CONFIG.errorFilters.invalidErrors.some(msg =>
      errorValue.includes(msg)
    )
  }

  // Filter SDK noise
  if (
    SENTRY_CONFIG.errorFilters.sdkNoise.some(msg => errorValue.includes(msg))
  ) {
    return true
  }

  // Filter errors from browser extensions or Safari privacy features
  // webkit-masked-url://hidden indicates masked extension/tracking code, not our app
  if (
    event?.exception?.values?.some(v =>
      v.stacktrace?.frames?.some(f =>
        f.filename?.includes("webkit-masked-url://hidden")
      )
    )
  ) {
    return true
  }

  // Filter network errors (from shared config)
  const message = (error?.message || "").toLowerCase()
  const isNetworkError = SENTRY_CONFIG.errorFilters.networkErrors.some(
    pattern => message.includes(pattern.toLowerCase())
  )

  return isNetworkError
}

/**
 * Convert regex patterns from JSON strings to RegExp objects
 * @returns {RegExp[]} Array of RegExp objects for trace propagation targets
 */
export function getTracePropagationTargets() {
  return SENTRY_CONFIG.tracePropagationTargets.map(
    pattern => new RegExp(pattern)
  )
}

/**
 * Build base Sentry config (used by both React and ERB)
 * @param {SentryConfigOptions} [options={}] - Optional config overrides
 * @returns {Pick<import('@sentry/browser').BrowserOptions, 'dsn' | 'sendDefaultPii' | 'release' | 'environment' | 'tracesSampleRate' | 'tracePropagationTargets' | 'replaysSessionSampleRate' | 'replaysOnErrorSampleRate'>} The Sentry configuration options
 */
export function getBaseSentryConfig() {
  return {
    dsn: SENTRY_CONFIG.dsn,
    sendDefaultPii: SENTRY_CONFIG.sendDefaultPii,
    release: process.env.SHORT_CIRCLE_SHA1,
    environment: process.env.BUGSNAG_ENV || process.env.NODE_ENV,
    tracePropagationTargets: getTracePropagationTargets(),
    tracesSampleRate: SENTRY_CONFIG.tracing.sampleRate,
    replaysSessionSampleRate: SENTRY_CONFIG.replay.sessionSampleRate,
    replaysOnErrorSampleRate: SENTRY_CONFIG.replay.errorSampleRate
  }
}
